/**
 * Public-site locale URL helpers.
 * Canonical English prefix is /en/ — /us/ redirects (Sprint 1B).
 * Dashboard/agent paths use src/i18n/routeGuards.js (not here).
 */
import { traceUsToEnRedirect } from "../i18n/routeTelemetry";

export const LOCALE_URL_PREFIXES = Object.freeze({
  he: "he",
  en: "en",
  el: "el",
});

export const URL_PREFIX_TO_I18N = Object.freeze({
  he: "he",
  en: "en",
  us: "en",
  el: "el",
});

export const I18N_TO_URL_PREFIX = Object.freeze({
  he: "he",
  en: "en",
  el: "el",
});

const LOCALE_PREFIX_PATTERN = /^\/(he|en|us|el)(?=\/|$)/;

const EXCLUDED_PREFIXES = [
  "/p/",
  "/p",
  "/dashboard",
  "/administration",
  "/admin",
  "/debug/",
  "/api/",
  "/packages/profile",
  "/packages/success",
];

function normalizePath(pathname) {
  if (!pathname || typeof pathname !== "string") return "/";
  const normalized = pathname.startsWith("/") ? pathname : `/${pathname}`;
  if (normalized.length > 1 && normalized.endsWith("/")) {
    return normalized.replace(/\/+$/, "") || "/";
  }
  return normalized;
}

export function isValidLocaleUrlPrefix(prefix) {
  return prefix === "he" || prefix === "en" || prefix === "el";
}

export function i18nLanguageToUrlPrefix(language) {
  const lng = String(language || "").toLowerCase();
  if (lng.startsWith("en")) return LOCALE_URL_PREFIXES.en;
  if (lng.startsWith("el")) return "el";
  return LOCALE_URL_PREFIXES.he;
}

export function urlPrefixToI18nLanguage(prefix) {
  return URL_PREFIX_TO_I18N[prefix] || "he";
}

export function parseLocaleFromPathname(pathname) {
  const path = normalizePath(pathname);
  const match = path.match(LOCALE_PREFIX_PATTERN);
  if (!match) {
    return { localePrefix: null, pathnameWithoutLocale: path };
  }
  let localePrefix = match[1];
  if (localePrefix === "us") {
    localePrefix = "en";
  }
  const pathnameWithoutLocale =
    path.replace(/^\/(he|en|us|el)(?=\/|$)/, "") || "/";
  return { localePrefix, pathnameWithoutLocale };
}

export function isExcludedFromLocalePrefix(pathname) {
  const path = normalizePath(pathname);
  if (LOCALE_PREFIX_PATTERN.test(path)) return false;
  return EXCLUDED_PREFIXES.some(
    (prefix) => path === prefix.replace(/\/$/, "") || path.startsWith(prefix)
  );
}

export function shouldRedirectToLocalePrefix(pathname) {
  const path = normalizePath(pathname);
  if (!path || path === "/") return true;
  if (LOCALE_PREFIX_PATTERN.test(path)) return false;
  return !isExcludedFromLocalePrefix(path);
}

/** Sprint 1B: /us/* → /en/* — legacy prefix must not remain active. */
export function shouldRedirectUsPrefixToEn(pathname) {
  const path = normalizePath(pathname);
  return path === "/us" || path.startsWith("/us/");
}

export function buildUsToEnRedirectPath(pathname, search = "", hash = "") {
  const path = normalizePath(pathname);
  const rest = path.replace(/^\/us(?=\/|$)/, "");
  const target = `/en${rest === "" ? "" : rest}`;
  traceUsToEnRedirect({ from: path, to: `${target}${search}${hash}` });
  return `${target}${search}${hash}`;
}

export function buildLocalizedPath(pathname, localePrefix = LOCALE_URL_PREFIXES.he) {
  const prefix =
    localePrefix === "us"
      ? LOCALE_URL_PREFIXES.en
      : isValidLocaleUrlPrefix(localePrefix) || localePrefix === "he"
        ? localePrefix === "el"
          ? "el"
          : localePrefix === "en"
            ? "en"
            : "he"
        : LOCALE_URL_PREFIXES.he;
  const path = normalizePath(pathname);
  const { pathnameWithoutLocale } = parseLocaleFromPathname(path);
  if (pathnameWithoutLocale === "/") {
    return `/${prefix}/`;
  }
  const stripped = pathnameWithoutLocale.replace(/^\/+/, "");
  return `/${prefix}/${stripped}`;
}

export function switchLocaleInPathname(pathname, targetLocalePrefix) {
  const { pathnameWithoutLocale } = parseLocaleFromPathname(pathname);
  return buildLocalizedPath(pathnameWithoutLocale, targetLocalePrefix);
}
